import { useEffect, useMemo, useState } from 'react';
import { Search, Users, MapPin, ExternalLink, Plus, Mail, Phone } from 'lucide-react';
import { useClientStore } from '../lib/store';
import type { Client } from '../lib/store';
import EmptyState from '../components/EmptyState';
import ClientFormModal from '../components/ClientFormModal';

export default function Clients() {
  const { clients, fetchClients, loading } = useClientStore();
  const [q, setQ] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return clients;
    return clients.filter(
      (c) =>
        c.name.toLowerCase().includes(ql) ||
        (c.email || '').toLowerCase().includes(ql) ||
        (c.phone || '').toLowerCase().includes(ql) ||
        c.addresses.some((a) => a.address_line.toLowerCase().includes(ql))
    );
  }, [clients, q]);

  const openNew = () => {
    setEditingClient(null);
    setModalOpen(true);
  };
  const openEdit = (c: Client) => {
    setEditingClient(c);
    setModalOpen(true);
  };

  const afterSave = () => {
    // Simplest: refetch the list from server so the store stays canonical.
    fetchClients();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Clients</h1>
          <p className="text-slate-500 mt-1">Your customer directory.</p>
        </div>
        <button onClick={openNew} className="btn btn-primary">
          <Plus className="h-4 w-4" /> Add client
        </button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          type="text"
          placeholder="Search by name, email, phone or address…"
          className="input pl-9"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {loading && clients.length === 0 ? (
        <div className="card p-8 text-center">
          <div className="animate-spin h-6 w-6 rounded-full border-3 border-brand-600 border-t-transparent mx-auto" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="card p-8">
          <EmptyState
            icon={Users}
            title={q ? 'No matches' : 'No clients yet'}
            subtitle={q ? 'Try a different search.' : 'Tap "Add client" above to create your first customer.'}
          />
        </div>
      ) : (
        <div className="card overflow-hidden">
          {/* Desktop table */}
          <div className="hidden md:block">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="text-left font-semibold text-slate-500 uppercase tracking-wider text-xs px-6 py-3">
                    Name
                  </th>
                  <th className="text-left font-semibold text-slate-500 uppercase tracking-wider text-xs px-6 py-3">
                    Contact
                  </th>
                  <th className="text-left font-semibold text-slate-500 uppercase tracking-wider text-xs px-6 py-3">
                    Addresses
                  </th>
                  <th className="text-left font-semibold text-slate-500 uppercase tracking-wider text-xs px-6 py-3">
                    Xero
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => openEdit(c)}
                    className="hover:bg-slate-50 cursor-pointer transition"
                  >
                    <td className="px-6 py-4">
                      <div className="font-semibold text-slate-900">{c.name}</div>
                    </td>
                    <td className="px-6 py-4 text-slate-600">
                      <div className="space-y-1">
                        {c.email && (
                          <div className="flex items-center gap-1.5 text-xs">
                            <Mail className="h-3.5 w-3.5 text-slate-400" />
                            <span>{c.email}</span>
                          </div>
                        )}
                        {c.phone && (
                          <div className="flex items-center gap-1.5 text-xs">
                            <Phone className="h-3.5 w-3.5 text-slate-400" />
                            <span>{c.phone}</span>
                          </div>
                        )}
                        {!c.email && !c.phone && <span className="text-slate-400 italic text-xs">—</span>}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-600">
                      {c.addresses.length === 0 ? (
                        <span className="text-slate-400 text-sm italic">No address</span>
                      ) : (
                        <ul className="space-y-1">
                          {c.addresses.slice(0, 3).map((a, i) => (
                            <li key={i} className="flex items-start gap-1.5 text-sm">
                              <MapPin className="h-3.5 w-3.5 shrink-0 mt-0.5 text-slate-400" />
                              <span>{a.address_line}</span>
                            </li>
                          ))}
                          {c.addresses.length > 3 && (
                            <li className="text-xs text-slate-400">
                              +{c.addresses.length - 3} more
                            </li>
                          )}
                        </ul>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {c.xero_customer_id ? (
                        <span className="chip bg-emerald-100 text-emerald-800 ring-1 ring-inset ring-emerald-200">
                          <ExternalLink className="h-3 w-3" /> Linked
                        </span>
                      ) : (
                        <span className="text-slate-400 text-xs">Not linked</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile card list */}
          <div className="md:hidden divide-y divide-slate-100">
            {filtered.map((c) => (
              <button
                type="button"
                key={c.id}
                onClick={() => openEdit(c)}
                className="w-full text-left p-4 hover:bg-slate-50 transition"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="font-semibold text-slate-900">{c.name}</div>
                  {c.xero_customer_id && (
                    <span className="chip bg-emerald-100 text-emerald-800 ring-1 ring-inset ring-emerald-200 text-[10px]">
                      <ExternalLink className="h-3 w-3" /> Xero
                    </span>
                  )}
                </div>
                {c.email && (
                  <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-1">
                    <Mail className="h-3.5 w-3.5" /> {c.email}
                  </div>
                )}
                {c.phone && (
                  <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-0.5">
                    <Phone className="h-3.5 w-3.5" /> {c.phone}
                  </div>
                )}
                {c.addresses.slice(0, 2).map((a, i) => (
                  <div key={i} className="flex items-start gap-1.5 text-sm text-slate-500 mt-1">
                    <MapPin className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    <span>{a.address_line}</span>
                  </div>
                ))}
                {c.addresses.length > 2 && (
                  <div className="text-xs text-slate-400 mt-1">+{c.addresses.length - 2} more</div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {modalOpen && (
        <ClientFormModal
          client={editingClient}
          onClose={() => {
            setModalOpen(false);
            setEditingClient(null);
          }}
          onSaved={() => {
            afterSave();
          }}
        />
      )}
    </div>
  );
}
